import { memo, useContext, useEffect, useMemo, useState } from 'react';
import { Button, message, Tooltip, Modal } from 'antd';
import { FieldArray, useFormikContext } from 'formik';
import type { FieldArrayRenderProps } from 'formik';
import { useTranslate } from '@tolgee/react';
import useSmartField from 'hooks/useSmartField';
import { useSpuKnowledgeMutate } from '@knowledge/knowledges/spu/crud';
import { FormOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';
import {
  set,
  cloneDeep,
  get,
  differenceBy,
  map,
  uniq,
  filter,
  pick,
  keys,
  find,
  omit,
  sortBy,
} from 'lodash-es';
import KnowledgeContentItem from '@knowledge/knowledges/spu/createOrEdit/formItem/KnowledgeContent';
import { useRefreshCurrentKnowledge, useUpdateOneKnowledgeInKcList } from './connect';
import { CurrentKnowledgeValueContext } from './context';
import { countSku } from '@knowledge/common/helpers/sku';
import { contentRender } from '../spu/Knowledge';
import { useGetSegmentsValue } from '@knowledge/common/share';
import { useCurrentSpuList } from '@knowledge/knowledges/singleClass/state';
import { useMemoizedFn } from 'ahooks';
import { overseaTranslate } from 'services/api/overseaTranslate';
import { regionLanguageMap } from 'services/config/region';
import {
  ValueAsDirectAnswerDrawer,
  ValueAsDirectAnswerDraweSelfProps,
} from '@knowledge/knowledges/singleClass/ValueAsDirectAnswerDrawer';
import useModal from 'hooks/useModal';
import {
  ConfigDefaultCheckbox,
  ConfigDefaultIcon,
  useSetCheck,
} from '@knowledge/knowledges/singleClass/ConfigDefault';
import { useMutation } from '@tanstack/react-query';
import { setKnowledgeDefault } from '@knowledge/knowledges/singleClassNew/api';
import { v4 } from 'uuid';

const ValuesArea = memo(({ citeMode, id }: { citeMode?: boolean; id: number }) => {
  const { field } = useSmartField<Knowledge.SPU.KnowledgeValue[]>('values');
  const { field: valueAsDirectAnswerField } = useSmartField<boolean>('valueAsDirectAnswer');
  const kcValues = field.value;

  return (
    <FieldArray name="values">
      {(arrayHelpers) => {
        return (
          <div className="flex-shrink-0 overflow-hidden flex flex-col basis-[38%] min-w-[469px] border-0 border-solid border-r border-[#f0f2f5]">
            <div className="overflow-auto" id={`box-container-${id}`}>
              {kcValues.map((v, index) => {
                return (
                  <KnowledgeContent
                    citeMode={citeMode}
                    firstId={kcValues[0].id}
                    valueAsDirectAnswer={valueAsDirectAnswerField.value}
                    value={v}
                    key={v.id}
                    index={index}
                    arrayHelpers={arrayHelpers}
                  />
                );
              })}
            </div>
            <NewKnowledgeContent
              citeMode={citeMode}
              arrayHelpers={arrayHelpers}
              valueAsDirectAnswer={valueAsDirectAnswerField.value}
            />
          </div>
        );
      }}
    </FieldArray>
  );
});

const KnowledgeContent = memo(
  (props: {
    citeMode?: boolean;
    firstId?: string;
    value: Knowledge.SingleClass.Knowledge['values'][number];
    index: number;
    arrayHelpers: FieldArrayRenderProps;
    valueAsDirectAnswer: boolean;
  }) => {
    const { citeMode, firstId, value, index, valueAsDirectAnswer } = props;
    const { t } = useTranslate();
    const { getSegmentsValue } = useGetSegmentsValue(valueAsDirectAnswer);
    const [isEdit, setIsEdit] = useState(false);
    const { newUpdate } = useSpuKnowledgeMutate();
    const { deleteUpdate } = useUpdateOneKnowledgeInKcList();
    const refresh = useRefreshCurrentKnowledge();
    const { values, setValues, handleSubmit } = useFormikContext<Knowledge.SPU.Knowledge>();
    const spuList = useCurrentSpuList();
    const regions = useMemo(() => {
      return uniq(
        map(spuList, (item) => {
          return item.region;
        }),
      );
    }, [spuList]);
    const { visible, show, hide, meta } = useModal<ValueAsDirectAnswerDraweSelfProps>();
    const [deleteLoading, setDeleteLoading] = useState(false);
    const regionKCItemDefaultValues = useMemo<
      Record<General.UpperCaseRegion, Knowledge.SPU.Segment[]> | undefined
    >(() => {
      try {
        const map = JSON.parse(value.segmentsMultiLang);
        const rmap = pick(map, regions);
        if (keys(rmap).length === 0) {
          rmap[regions[0]] = getSegmentsValue(value);
        }
        return rmap;
      } catch (e) {
        console.log(e);
        return undefined;
      }
    }, [regions, value, getSegmentsValue]);
    const { active, setActive } = useContext(CurrentKnowledgeValueContext);
    const skuCount = useMemo(() => {
      return countSku(get(values, `values.${index}.spus`, []), 'skus');
    }, [values, index]);
    const [loading, setLoading] = useState(false);
    const hasDefaultValue = useMemo(() => {
      return !!find(values.values, (e) => e.isDefaultValue && e.id !== value.id);
    }, [values, value]);
    const [checked, setChecked] = useState(value.isDefaultValue);
    const { mutateAsync } = useMutation({
      mutationFn: setKnowledgeDefault,
      onError: () => {
        message.error(t('translation.操作失败，请稍后再试'));
      },
    });
    const replaceItem = useMemoizedFn((nextValues?: Knowledge.SPU.Knowledge) => {
      setLoading(true);

      Promise.all([
        nextValues
          ? newUpdate({
              spuKnowledge: nextValues,
            })
          : Promise.resolve(),
        checked !== value.isDefaultValue
          ? mutateAsync({
              spuKnowledgeDefinitionId: values.id,
              defaultValueId: value.id!,
            })
          : Promise.resolve(),
      ])
        .then(() => {
          return refresh().then(() => {
            setIsEdit(false);
            hide();
            if (checked) {
              requestAnimationFrame(() => {
                const scrollContainer = document.getElementById(`box-container-${values.id}`);
                scrollContainer?.scrollTo({
                  top: 0,
                  behavior: 'smooth',
                });
              });
            }
            message.success(t('operation.saveSucceed'));
          });
        })
        .finally(() => {
          setLoading(false);
        });
    });
    const checkReplaceItem = useSetCheck(replaceItem, () => checked !== value.isDefaultValue, {
      isChange: checked && hasDefaultValue,
      isCancel: !checked && !hasDefaultValue,
    });
    useEffect(() => {
      setChecked(value.isDefaultValue);
    }, [value]);

    const deleteItem = useMemoizedFn(() => {
      Modal.confirm({
        title: t('knowledge.确认要删除这条商品知识吗？'),
        onOk: () => {
          const nextValues = cloneDeep(values);
          nextValues.values.splice(index, 1);
          if (citeMode) {
            setValues(nextValues);
            handleSubmit();
            return;
          }
          setDeleteLoading(true);
          return deleteUpdate(nextValues)
            .then(() => {
              return refresh().then(() => {
                setIsEdit(false);
                setActive('');
                hide();
                requestAnimationFrame(() => {
                  const scrollContainer = document.getElementById(`box-container-${value.id}`);
                  scrollContainer?.scrollTo({
                    top: 0,
                    behavior: 'smooth',
                  });
                });
              });
            })
            .finally(() => setDeleteLoading(false));
        },
      });
    });

    if (!isEdit) {
      return (
        <div className="h-[96px] border-0 border-solid border-b border-[#f0f2f5] flex">
          <div className="flex-grow basis-0 px-[16px] py-[12px] overflow-hidden">
            <div className="bg-[#f4f6f8] leading-[20px] py-[12px] px-[14px] text-[#515359] h-full flex items-center relative">
              {value.isDefaultValue && (
                <div className="absolute left-0 top-0 bg-[#EBFAF3] leading-[14px] text-[#00BF69] text-[12px] px-[4px]">
                  {t('默认值')}
                </div>
              )}
              {contentRender(getSegmentsValue(value))}
              {meta && visible && (
                <ValueAsDirectAnswerDrawer
                  citeMode={citeMode}
                  {...meta}
                  visible={visible}
                  saveLoading={loading}
                  deleteLoading={deleteLoading}
                  onClose={() => hide()}
                  onDelete={() => {
                    deleteItem();
                  }}
                  setDefaultNode={
                    !citeMode && (
                      <ConfigDefaultCheckbox
                        checked={checked}
                        onChange={(e) => {
                          setChecked(e.target.checked);
                        }}
                      />
                    )
                  }
                  onConfirm={(res) => {
                    const copy = cloneDeep(values);
                    const segmentsMultiLang = JSON.stringify(res);
                    const getV = get(copy, `values[${index}]`);
                    let nextV = Object.assign(getV, {
                      segments: Object.values(res)?.[0] || [],
                      segmentsMultiLang,
                    });
                    if (citeMode) {
                      copy.values[index] = nextV;
                      setValues(copy);
                      handleSubmit();
                      hide();
                      return;
                    }
                    nextV = omit(nextV, 'spus');
                    const nextValues = set(copy, `values`, [nextV]);
                    checkReplaceItem(nextValues);
                  }}
                />
              )}
              <div className="flex flex-col items-center justify-between">
                <ConfigDefaultIcon
                  isDefaultValue={value.isDefaultValue}
                  knowledgeId={values.id}
                  knowledgeValueId={value.id}
                  hasDefaultValue={hasDefaultValue}
                  onSuccess={() => {
                    refresh();
                  }}
                  onCiteModeClick={() => {
                    setValues((pre) => {
                      return {
                        ...pre,
                        values: sortBy(
                          pre.values.map((cur) => {
                            return {
                              ...cur,
                              isDefaultValue:
                                `${cur.id}` === `${value.id}` ? !value.isDefaultValue : false,
                            };
                          }),
                          ({ id, isDefaultValue }) => (id === firstId ? 0 : isDefaultValue ? 1 : 2),
                        ),
                      };
                    });
                    handleSubmit();
                  }}
                />
                <Tooltip title={t('marketing.编辑')} placement="bottom">
                  <Button
                    type="text"
                    icon={<FormOutlined style={{ color: '#56575a' }} />}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (valueAsDirectAnswer) {
                        show({
                          regions: citeMode
                            ? Object.keys(regionKCItemDefaultValues || {})
                            : regions,
                          defaultValues: regionKCItemDefaultValues,
                        });
                      } else {
                        setIsEdit(true);
                      }
                    }}
                  />
                </Tooltip>
              </div>
            </div>
          </div>
          <ToggleArea
            onClick={() => setActive(value.id)}
            active={active === value.id}
            className="w-[140px] flex-shrink-0 border-0 border-solid border-l border-[#f0f2f5] bg-[#f7f9fa] flex items-center pl-[16px] cursor-pointer"
          >
            {t('config_yet', { x: skuCount })}
          </ToggleArea>
        </div>
      );
    }

    return (
      <KnowledgeContentItemContainer className="bg-[#f4f6f8] knowledge-content-item">
        <KnowledgeContentItem
          showHeader
          value={getSegmentsValue(value)}
          shortcut
          loading={loading}
          onOk={(next) => {
            if (citeMode) {
              const nextValues = {
                ...values,
                values: values.values.map((cur) => {
                  return {
                    ...cur,
                    ...(cur.id === value.id ? next : undefined),
                    isDefaultValue:
                      cur.id === value.id ? checked : checked ? false : cur.isDefaultValue,
                  };
                }),
              };
              setValues(nextValues);
              handleSubmit();
              setIsEdit(false);
              return;
            }
            if (next) {
              const copy = cloneDeep(values) as Knowledge.SPU.Knowledge;
              const getV = get(copy, `values[${index}]`);
              let nextV = Object.assign(getV, next);
              nextV = omit(nextV, 'spus');
              const nextValues = set(copy, `values`, [nextV]);
              checkReplaceItem(nextValues);
            } else {
              checkReplaceItem();
            }
          }}
          onCancel={() => {
            setIsEdit(false);
          }}
          isDirty={checked !== value.isDefaultValue}
          setDefaultNode={
            <ConfigDefaultCheckbox
              checked={checked}
              onChange={(e) => {
                setChecked(e.target.checked);
              }}
            />
          }
          onDelete={deleteItem}
        />
      </KnowledgeContentItemContainer>
    );
  },
);

KnowledgeContent.displayName = 'KnowledgeContent';

const NewKnowledgeContent = memo(
  (props: {
    citeMode?: boolean;
    arrayHelpers: FieldArrayRenderProps;
    valueAsDirectAnswer: boolean;
  }) => {
    const { citeMode, valueAsDirectAnswer = false } = props;
    const { t } = useTranslate();
    const [isEdit, setIsEdit] = useState(false);
    const { update } = useSpuKnowledgeMutate();
    const { setActive } = useContext(CurrentKnowledgeValueContext);
    const [loading, setLoading] = useState(false);
    const { values, setValues, handleSubmit } = useFormikContext<Knowledge.SPU.Knowledge>();
    const spuList = useCurrentSpuList();
    const regions = useMemo(() => {
      return uniq(
        map(spuList, (item) => {
          return item.region;
        }),
      );
    }, [spuList]);
    const refresh = useRefreshCurrentKnowledge();
    const hasDefaultValue = useMemo(() => {
      return !!find(values.values, (e) => e.isDefaultValue);
    }, [values]);
    const insertItem = useMemoizedFn((nextValues: Knowledge.SPU.Knowledge) => {
      setLoading(true);
      update({
        spuKnowledge: nextValues,
      })
        .then(() => {
          return refresh().then((res) => {
            const diffK = differenceBy(res!.values, values.values, 'id');
            if (diffK?.length > 0) {
              setActive(diffK[0].id);
              setIsEdit(false);
              hide();
              requestAnimationFrame(() => {
                const scrollContainer = document.getElementById(`box-container-${values.id}`);
                scrollContainer?.scrollTo({
                  top: get(diffK, '0.isDefaultValue') ? 0 : 10000000,
                  behavior: 'smooth',
                });
              });
              message.success(t('operation.saveSucceed'));
            } else {
              message.error(t('translation.操作失败，请稍后再试'));
            }
          });
        })
        .finally(() => {
          setLoading(false);
        });
    });
    const [checked, setChecked] = useState(false);
    const confirmHasSet = useSetCheck(insertItem, () => checked, {
      isChange: hasDefaultValue,
    });
    useEffect(() => {
      setChecked(false);
    }, [isEdit]);
    const { visible, show, hide, meta } = useModal<ValueAsDirectAnswerDraweSelfProps>();

    if (!isEdit) {
      return (
        <div className="basis-[56px] flex-shrink-0 flex-grow">
          {meta && visible && (
            // 新增
            <ValueAsDirectAnswerDrawer
              citeMode={citeMode}
              {...meta}
              visible={visible}
              saveLoading={loading}
              onClose={() => {
                setChecked(false);
                hide();
              }}
              setDefaultNode={
                <ConfigDefaultCheckbox
                  checked={checked}
                  onChange={(e) => setChecked(e.target.checked)}
                />
              }
              onConfirm={(res) => {
                const nextValues = cloneDeep(values);
                const segmentsMultiLang = JSON.stringify(res);
                if (citeMode) {
                  if (checked) {
                    nextValues.values.forEach((cur) => (cur.isDefaultValue = false));
                  }
                  nextValues.values.push({
                    id: `fake-${v4()}`,
                    segments: Object.values(res)?.[0] || [],
                    segmentsMultiLang,
                    isDefaultValue: checked,
                    spus: [],
                  } as Knowledge.SPU.KnowledgeValue);
                  setValues(nextValues);
                  handleSubmit();
                  hide();
                  return;
                }
                nextValues.values.push({
                  segments: Object.values(res)?.[0] || [],
                  segmentsMultiLang,
                  isDefaultValue: checked,
                } as Knowledge.SPU.KnowledgeValue);
                confirmHasSet(nextValues);
              }}
            />
          )}
          <Button
            onClick={() => {
              if (valueAsDirectAnswer) {
                show({
                  defaultValues: undefined,
                  regions: citeMode ? ['GB'] : regions,
                });
              } else {
                setIsEdit(true);
              }
            }}
            type="link"
          >
            +{t('knowledge.新增x', { x: t('knowledge.知识值') })}
          </Button>
        </div>
      );
    }

    return (
      <KnowledgeContentItemContainer className="bg-[#f4f6f8] basis-[216px] flex-shrink-0 knowledge-content-item">
        <KnowledgeContentItem
          shortcut
          onOk={(next) => {
            const nextValues = cloneDeep(values);
            if (citeMode) {
              if (checked) {
                nextValues.values.forEach((cur) => (cur.isDefaultValue = false));
              }
              const id = `fake-${v4()}`;
              nextValues.values.push({
                id,
                isDefaultValue: checked,
                spus: [],
                segmentsMultiLang: '',
                ...next,
              } as Knowledge.SPU.KnowledgeValue);
              setValues(nextValues);
              handleSubmit();
              setActive(id);
              setIsEdit(false);
              hide();
              return;
            }
            nextValues.values.push(
              Object.assign({ isDefaultValue: checked }, next) as Knowledge.SPU.KnowledgeValue,
            );
            confirmHasSet(nextValues);
          }}
          loading={loading}
          onCancel={() => {
            setIsEdit(false);
          }}
          setDefaultNode={
            <ConfigDefaultCheckbox
              checked={checked}
              onChange={(e) => {
                setChecked(e.target.checked);
              }}
            />
          }
        />
      </KnowledgeContentItemContainer>
    );
  },
);

NewKnowledgeContent.displayName = 'NewKnowledgeContent';

const KnowledgeContentItemContainer = styled.div`
  .ant-input-textarea::after {
    bottom: 6px !important;
  }
`;

const ToggleArea = styled.div<{ active: boolean }>`
  position: relative;
  color: ${({ active }) => (active ? '#336fff' : '#515359')};
  &::after {
    content: '';
    position: absolute;
    width: 2px;
    right: 0;
    top: 0;
    bottom: 0;
    background: #336fff;
    display: ${({ active }) => (active ? 'block' : 'none')};
  }
`;

export const translateSegment = async (
  segments: Knowledge.SPU.Segment[],
  region?: General.UpperCaseRegion,
  lang?: General.SystemLanguage,
) => {
  const texts = map(
    filter(segments, (e) => e.type === 'TEXT'),
    'content',
  );
  const overwriteRegionLanguageMap = {
    ...regionLanguageMap,
    ph: 'tl',
    my: 'ms',
  } as Record<General.Region, General.Language>;
  const targetLang = region
    ? overwriteRegionLanguageMap[region.toLowerCase() as General.Region]
    : lang || 'en-US';
  try {
    const res = await overseaTranslate(texts, targetLang, 'singleClass', region);
    let index = 0;
    return map(segments, (segment) => {
      if (segment.type === 'TEXT') {
        return {
          ...segment,
          content: res[index++],
        };
      }
      return segment;
    });
  } catch (_) {
    return segments;
  }
};

export default ValuesArea;
